import React from 'react';
import { Text, View } from '@react-pdf/renderer';
import { RHeader, RPage, RViewer } from '../../../../components/PdfRender';
import { RSignature } from '../../../../components/PdfRender/RSignature';
import { useCompany } from '../../../../hooks/company';
import { IFinancial } from '../../../interfaces';
import * as S from './styles';
import { currency } from '../../../utils/functions';
import { formatDate } from '../../../utils/format-date';
import extenso from 'extenso';
import { useAuth } from '../../../../hooks/auth';

interface IFinancialReceiptReportProps {
  financial: IFinancial | null;
}

export const FinancialReceiptReport: React.FC<IFinancialReceiptReportProps> = ({
  financial,
}) => {
  const { company } = useCompany();
  const { clinic } = useAuth();
  const financialValue = financial?.value ? +financial?.value : 0;

  const renderReceiptContent = () => (
    <View>
      <RHeader company={company} />

      <View style={S.styles.titleRow}>
        <View>
          <Text style={S.styles.titleText}>RECIBO</Text>
          {financial?.code ? (
            <Text style={S.styles.receiptNumber}>
              Nº {String(financial.code).padStart(4, '0')}
            </Text>
          ) : null}
        </View>
        <View style={S.styles.valueBox}>
          <Text style={S.styles.valueLabelText}>VALOR</Text>
          <Text style={S.styles.valueAmountText}>
            {currency(financialValue)}
          </Text>
        </View>
      </View>

      <View style={S.styles.descriptionBox}>
        <Text style={S.styles.descriptionText}>
          {'Recebi(emos) de '}
          <Text style={S.styles.textBold}>
            {financial?.client_name?.toUpperCase() ?? ''}
          </Text>
          {financial?.client_document
            ? `, portador(a) do CPF/CNPJ nº ${financial.client_document},`
            : ','}
          {' o valor de '}
          <Text style={S.styles.textBold}>
            {extenso(financialValue, { mode: 'currency' })?.toUpperCase()}
          </Text>
          {financial?.description
            ? `, referente a ${financial.description}`
            : ''}
          {'. Para clareza, firmo(amos) o presente recibo.'}
        </Text>
      </View>

      {(financial?.method_payment || financial?.number_doc) && (
        <View style={S.styles.detailsRow}>
          {financial?.method_payment && (
            <View style={S.styles.detailItem}>
              <Text style={S.styles.detailLabel}>FORMA DE PAGAMENTO</Text>
              <Text style={S.styles.detailValue}>
                {financial.method_payment}
              </Text>
            </View>
          )}
          {financial?.number_doc && (
            <View style={S.styles.detailItem}>
              <Text style={S.styles.detailLabel}>Nº DO DOCUMENTO</Text>
              <Text style={S.styles.detailValue}>{financial.number_doc}</Text>
            </View>
          )}
        </View>
      )}

      {financial?.observation ? (
        <View style={S.styles.observationBox}>
          <Text style={S.styles.observationLabel}>Observação</Text>
          <Text style={S.styles.observationText}>{financial.observation}</Text>
        </View>
      ) : null}

      <View style={S.styles.dateRow}>
        <Text style={S.styles.dateText}>
          {company?.city ? `${company.city}, ` : ''}
          {formatDate(financial?.payment_date) ??
            formatDate(financial?.due_date)}
        </Text>
      </View>

      <RSignature
        professional_name={company?.corporate_name}
        terminology_description=" "
        terminology_code={company?.cpf_cnpj}
        clinic={clinic}
      />
    </View>
  );

  return (
    <RViewer>
      <RPage size={'A4'}>
        <View style={S.styles.pageContainer}>
          <View style={S.styles.viaSection}>
            <Text style={S.styles.viaLabel}>1ª Via – Original</Text>
            {renderReceiptContent()}
          </View>

          <View style={S.styles.cutLineRow}>
            <View style={S.styles.cutLinePart} />
            <Text style={S.styles.cutLineText}>Recorte aqui</Text>
            <View style={S.styles.cutLinePart} />
          </View>

          <View style={S.styles.viaSection}>
            <Text style={S.styles.viaLabel}>2ª Via – Cópia</Text>
            {renderReceiptContent()}
          </View>
        </View>
      </RPage>
    </RViewer>
  );
};
